import React, { useEffect, useState } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import { hasPurchasingSettingsPermission } from "../../../utils/purchasingSettingsPermissions";
import OrdersSettingsTab from "./OrdersSettingsTab";
import ServiceOrdersSettingsTab from "./ServiceOrdersSettingsTab";
import StockRequisitionPoliciesTab from "./StockRequisitionPoliciesTab";
import CatalogCreationApprovalSettingsTab from "../catalogApprovals/CatalogCreationApprovalSettingsTab";
import { catalogMessages } from "../catalogApprovals/catalogApprovalMessages";
import "./ApprovalSettingsTab.css";

export default function ApprovalSettingsTab({ canManagePurchasing = true, canConfigureServices = false, businessUnitId }) {
  const { languageId } = usePmsTheme();
  const spanish = languageId === "es";
  const catalan = languageId === "ca";
  const t = catalogMessages(languageId);
  const [, refreshSession] = useState(0);
  const currentUser = readStoredUser();
  useEffect(() => {
    const update = () => refreshSession((version) => version + 1);
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener("palantir-session-updated", update); window.removeEventListener("storage", update); };
  }, []);
  const [activeTab, setActiveTab] = useState(canManagePurchasing ? "orders" : "services");
  const tabs = [
    { id: "orders", scope: "orders", label: spanish ? "Órdenes de compra" : catalan ? "Comandes de compra" : "Purchase orders" },
    { id: "stock", scope: "stock_requisitions", label: spanish ? "Solicitudes de existencias" : catalan ? "Sol·licituds d’existències" : "Stock requisitions" },
    { id: "transfers", scope: "stock_transfers", label: spanish ? "Traspasos de existencias" : catalan ? "Traspassos d’existències" : "Stock Transfers" },
    { id: "catalog", scope: "catalog", label: t("Catalog records") },
    { id: "services", label: spanish ? "Órdenes de servicio" : catalan ? "Ordres de servei" : "Service orders" },
  ].filter((tab) => tab.id === "services" ? canConfigureServices : canManagePurchasing && hasPurchasingSettingsPermission(currentUser, tab.scope, "view"));
  const selectedTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : tabs[0]?.id;
  useEffect(() => { if (selectedTab && activeTab !== selectedTab) setActiveTab(selectedTab); }, [activeTab, selectedTab]);
  if (!tabs.length) return null;

  return <section className="approvalSettings" aria-label={spanish ? "Aprobaciones y permisos" : catalan ? "Aprovacions i permisos" : "Approvals & Permissions"}>
    <div className="approvalSettings__tabs" role="tablist" aria-label={spanish ? "Tipo de documento" : catalan ? "Tipus de document" : "Document type"}>
      {tabs.map((tab, index) => <button key={tab.id} type="button" role="tab"
        id={`approval-tab-${tab.id}`} aria-controls={`approval-panel-${tab.id}`}
        aria-selected={selectedTab === tab.id} tabIndex={selectedTab === tab.id ? 0 : -1}
        onClick={() => setActiveTab(tab.id)} onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === "Home" ? tabs[0] : event.key === "End" ? tabs[tabs.length - 1] : tabs[(index + (event.key === "ArrowLeft" ? tabs.length - 1 : 1)) % tabs.length];
          setActiveTab(next.id);
          event.currentTarget.parentElement.querySelector(`#approval-tab-${next.id}`)?.focus();
        }}>{tab.label}</button>)}
    </div>
    <div className="approvalSettings__panel" role="tabpanel" id={`approval-panel-${selectedTab}`} aria-labelledby={`approval-tab-${selectedTab}`}>
      {selectedTab === "services" ? <ServiceOrdersSettingsTab businessUnitId={businessUnitId} /> : selectedTab === "catalog" ? <CatalogCreationApprovalSettingsTab /> : selectedTab === "orders" ? <OrdersSettingsTab /> : <StockRequisitionPoliciesTab key={selectedTab} policyType={selectedTab === "transfers" ? "transfers" : "requisitions"} />}
    </div>
  </section>;
}
